/* The existing discount property is deliberately preserved: _BrandGwp=selector. */
(() => {
  const CAMPAIGN = 'wie-birthday-2026';
  const id = (value) => String(value || '');
  const ids = (value) => new Set(String(value || '').split(',').map((v) => v.trim()).filter(Boolean));
  // Missing VIP-only settings keep link/session access required for older saved sections.
  const activeOffer = (config, access) => config.enabled === true && config.country === 'AU' &&
    (config.vipOnly === false || (Boolean(config.offerKey) && access === true));
  const qualifies = (cart, config) => (cart.items || []).some((item) =>
    ids(config.qualifyingVariantIds).has(id(item.variant_id)) && item.quantity > 0 &&
    !item.properties?._BrandGwp && !item.properties?._discount_kit_offer && !item.parent_relationship?.parent
  );
  const availableIds = (config) => new Set((config.gifts || []).filter((gift) => gift.available === true && gift.variantId).map((gift) => id(gift.variantId)));
  const giftLines = (cart, config, active) => (cart.items || []).filter((item) =>
    item.properties?._BrandGwp === 'selector' &&
    (active || item.properties?._WIEBirthdayOffer === CAMPAIGN || (config.gifts || []).some((gift) => id(gift.variantId) === id(item.variant_id)))
  );
  const normalizationPlan = (cart, config, active) => {
    const updates = {};
    // Replace only gifts marked by the two older automatic campaigns in a birthday session.
    if (active) for (const item of cart.items || []) {
      if (['product', 'threshold'].includes(item.properties?._BrandGwp)) updates[item.key] = 0;
    }
    const eligible = active && qualifies(cart, config);
    const allowed = availableIds(config);
    let kept = false;
    for (const item of giftLines(cart, config, active)) {
      if (!eligible || !allowed.has(id(item.variant_id)) || kept) updates[item.key] = 0;
      else { kept = true; if (item.quantity !== 1) updates[item.key] = 1; }
    }
    return updates;
  };
  const completeGift = (cart, config, active) => {
    const gifts = giftLines(cart, config, active);
    return active && qualifies(cart, config) && gifts.length === 1 && gifts[0].quantity === 1 &&
      availableIds(config).has(id(gifts[0].variant_id)) &&
      !(cart.items || []).some((item) => ['product', 'threshold'].includes(item.properties?._BrandGwp));
  };

  // Verify eligibility and one selected gift. Leave discount evaluation to checkout;
  // a nonzero Ajax cart price must not prevent the original select-gift-to-checkout flow.
  const selectGift = async (variantId, config, active, api, initialCart) => {
    if (!active || !availableIds(config).has(id(variantId))) throw new Error('This gift is unavailable. Please choose another.');
    let cart = initialCart || await api.cart();
    if (!qualifies(cart, config)) throw new Error('Add a 50ml or 100ml fragrance to choose your gift.');
    const legacy = (cart.items || []).filter((item) => ['product', 'threshold'].includes(item.properties?._BrandGwp));
    if (legacy.length) {
      await api.update(Object.fromEntries(legacy.map((item) => [item.key, 0])));
      cart = await api.cart();
      if (!qualifies(cart, config)) throw new Error('Your cart has changed. Add a 50ml or 100ml fragrance to choose your gift.');
    }
    const gifts = giftLines(cart, config, active);
    const alreadyChosen = gifts.length === 1 && id(gifts[0].variant_id) === id(variantId) && gifts[0].quantity === 1;
    try {
      if (!alreadyChosen) {
        if (gifts.length) {
          await api.update(Object.fromEntries(gifts.map((item) => [item.key, 0])));
          cart = await api.cart();
          if (!qualifies(cart, config)) throw new Error('Your cart has changed. Add a 50ml or 100ml fragrance to choose your gift.');
        }
        await api.add({ id: id(variantId), quantity: 1, properties: { _BrandGwp: 'selector', _WIEBirthdayOffer: CAMPAIGN } });
      }
      cart = await api.cart();
      if (!completeGift(cart, config, active)) throw new Error('Your complimentary gift could not be applied. Please try again.');
      return cart;
    } catch (error) {
      // Remove only promotional lines; a normally purchased Body Mist is preserved.
      try {
        const latest = await api.cart();
        const owned = giftLines(latest, config, active).filter((item) => id(item.variant_id) === id(variantId));
        if (owned.length) await api.update(Object.fromEntries(owned.map((item) => [item.key, 0])));
      } catch (_) { /* Keep checkout stopped if the network also prevents cleanup. */ }
      throw error;
    }
  };

  const rules = { activeOffer, qualifies, normalizationPlan, completeGift, selectGift };
  if (typeof module !== 'undefined' && module.exports) module.exports = rules;
  if (typeof document === 'undefined') return;
  const element = document.getElementById('WIEGwpConfig');
  if (!element) return;
  let config;
  try { config = JSON.parse(element.textContent); } catch (_) { return; }
  let active;
  const activate = () => {
    const storageKey = `wie-gwp:${CAMPAIGN}:${config.offerKey}`;
    let access = new URLSearchParams(window.location.search).get('offer') === config.offerKey && Boolean(config.offerKey);
    try {
      if (access) sessionStorage.setItem(storageKey, '1');
      access ||= sessionStorage.getItem(storageKey) === '1';
    } catch (_) { /* The offer link itself still works when session storage is unavailable. */ }
    active = activeOffer(config, access);
    window.WIEGiftSelector = { active };
  };
  activate();
  const checkoutSelector = '#CartDrawer-Checkout, #checkout, button[name="checkout"]';
  let overlay = document.getElementById('WIEGwpOverlay');
  if (!overlay) return;
  // Keep the single overlay outside replaced cart sections until it is opened.
  document.body.append(overlay);
  let cartState = { items: [] };
  let cartLoaded = false;
  let busy = false;
  let needsSync = false;
  let returnFocus;
  let selectionFeedback;
  const legacyVisibility = new Map();
  const routeRoot = window.Shopify?.routes?.root || '/';
  const request = async (resource, body) => {
    const response = await fetch(`${routeRoot}${resource}`, {
      credentials: 'same-origin',
      ...(body ? { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' }, body: JSON.stringify(body) } : {}),
    });
    const state = await response.json();
    if (!response.ok) throw new Error(state.description || state.message || 'Could not update your cart. Please try again.');
    return state;
  };
  const cartSectionIds = () => [...new Set([
    'cart-drawer', 'cart-icon-bubble',
    document.getElementById('main-cart-items')?.dataset.id,
    document.getElementById('main-cart-footer')?.dataset.id,
  ].filter(Boolean))];
  const renderCartSections = (state) => {
    const sections = state.sections || {};
    const parse = (name) => typeof sections[name] === 'string' ? new DOMParser().parseFromString(sections[name], 'text/html') : null;
    const drawerHtml = parse('cart-drawer');
    const drawer = document.querySelector('cart-drawer');
    const content = drawer?.querySelector('#CartDrawer');
    const sourceContent = drawerHtml?.querySelector('#CartDrawer');
    if (content && sourceContent) {
      content.innerHTML = sourceContent.innerHTML;
      drawer.classList.toggle('is-empty', !state.items.length);
      content.querySelector('#CartDrawer-Overlay')?.addEventListener('click', drawer.close.bind(drawer));
      const inner = content.querySelector('.drawer__inner');
      if (drawer.classList.contains('active') && inner && typeof trapFocus === 'function') trapFocus(inner, inner.querySelector('.drawer__close'));
    }
    const bubbleHtml = parse('cart-icon-bubble');
    const bubble = document.getElementById('cart-icon-bubble');
    const sourceBubble = bubbleHtml?.querySelector('#cart-icon-bubble, .shopify-section');
    if (bubble && sourceBubble) bubble.innerHTML = sourceBubble.innerHTML;
    const items = document.getElementById('main-cart-items');
    const sourceItems = parse(items?.dataset.id)?.querySelector('cart-items');
    if (sourceItems) document.querySelector('cart-items')?.replaceWith(sourceItems);
    const footer = document.getElementById('main-cart-footer');
    const sourceFooter = parse(footer?.dataset.id)?.querySelector('#main-cart-footer');
    if (footer && sourceFooter) footer.replaceWith(sourceFooter);
  };
  const api = {
    cart: async () => { const state = await request('cart.js'); cartLoaded = true; return state; },
    update: async (updates) => {
      const state = await request('cart/update.js', { updates, sections: cartSectionIds(), sections_url: window.location.pathname });
      // Commit the returned cart before replacing sections, including when a later read fails.
      cartState = state; cartLoaded = true; renderCartSections(state);
      return state;
    },
    add: (item) => request('cart/add.js', { items: [item] }),
  };
  const notifyCart = () => {
    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') publish(PUB_SUB_EVENTS.cartUpdate, { source: 'brand-gwp-selector' });
  };
  const showError = (error) => {
    const target = overlay.querySelector('[data-brand-gwp-error]');
    target.textContent = error.message || 'Could not apply your gift. Please try again.';
    target.hidden = false;
  };
  const close = () => {
    if (busy) return;
    overlay.hidden = true;
    document.body.append(overlay);
    if (typeof removeTrapFocus === 'function') removeTrapFocus(returnFocus);
    else returnFocus?.focus();
  };
  const open = (trigger) => {
    returnFocus = trigger || document.activeElement;
    const drawer = document.querySelector('cart-drawer.active .drawer__inner');
    overlay.dataset.brandGwpContext = drawer ? 'drawer' : 'page';
    (drawer || document.body).append(overlay);
    overlay.hidden = false;
    overlay.querySelector('[data-brand-gwp-error]').hidden = true;
    const first = overlay.querySelector('[data-brand-gwp-close]');
    if (typeof trapFocus === 'function') trapFocus(overlay, first);
    else first?.focus();
  };

  const updateUI = () => {
    if (cartLoaded && !cartState.items.length && !overlay.hidden) {
      overlay.hidden = true;
      document.body.append(overlay);
      if (typeof removeTrapFocus === 'function') removeTrapFocus(returnFocus);
    }
    if (!overlay.isConnected) {
      const drawer = !overlay.hidden && document.querySelector('cart-drawer.active .drawer__inner');
      overlay.dataset.brandGwpContext = drawer ? 'drawer' : 'page';
      (drawer || document.body).append(overlay);
    }
    const eligible = active && qualifies(cartState, config);
    const selected = completeGift(cartState, config, active);
    document.querySelectorAll(checkoutSelector).forEach((button) => {
      if (!button.dataset.wieOriginalLabel) {
        const original = button.dataset.brandCheckoutLabel || button.textContent.trim();
        button.dataset.wieOriginalLabel = original === config.checkoutLabel || original === 'Updating cart…' ? 'Checkout' : original;
      }
      const label = eligible && !selected ? config.checkoutLabel : button.dataset.wieOriginalLabel;
      if (button.dataset.brandCheckoutLocked) button.dataset.brandCheckoutLabel = label;
      else if (button.textContent.trim() !== label) button.textContent = label;
      if (busy) { button.dataset.wieGwpLocked = 'true'; button.disabled = true; }
      else if (button.dataset.wieGwpLocked) { delete button.dataset.wieGwpLocked; button.disabled = !cartState.items.length || Boolean(button.dataset.brandCheckoutLocked); }
      // A newly rendered checkout button may arrive after the cleanup's lock was released.
      if (cartLoaded && !cartState.items.length) { button.dataset.wieGwpEmpty = 'true'; button.disabled = true; button.setAttribute('aria-disabled', 'true'); }
      else if (!busy && button.dataset.wieGwpEmpty) { delete button.dataset.wieGwpEmpty; if (!button.disabled) button.removeAttribute('aria-disabled'); }
      const container = button.closest('.cart__ctas') || button.parentElement;
      let copy = container.querySelector('[data-wie-gwp-copy]') || (container.previousElementSibling?.matches('[data-wie-gwp-copy]') ? container.previousElementSibling : null);
      if (!copy && active) { copy = document.createElement('p'); copy.className = 'wie-gwp-cart-copy'; copy.dataset.wieGwpCopy = ''; copy.setAttribute('aria-live', 'polite'); container.before(copy); container.dataset.wieGwpCopyOwner = 'true'; }
      if (copy) { const text = selected ? config.successCopy : eligible ? config.qualifiedCopy : config.offerCopy; if (copy.textContent !== text) copy.textContent = text; copy.hidden = !active; copy.dataset.wieGwpState = selected ? 'selected' : eligible ? 'qualified' : 'offer'; }
    });
    document.querySelectorAll('.shopify-payment-button, .additional-checkout-buttons').forEach((node) => node.classList.toggle('wie-gwp-payment-hidden', active));
    if (active) {
      document.querySelectorAll('[data-brand-product-gwp], [data-brand-product-gwp-offer], [data-brand-progress="gift"]').forEach((node) => {
        if (!legacyVisibility.has(node)) legacyVisibility.set(node, node.hidden);
        node.hidden = true;
        node.classList.add('wie-gwp-legacy-hidden');
      });
    } else {
      for (const [node, hidden] of legacyVisibility) { node.hidden = hidden; node.classList.remove('wie-gwp-legacy-hidden'); }
      legacyVisibility.clear();
    }
    birthdayCopy();
  };
  const sync = async () => {
    if (busy) { needsSync = true; return; }
    busy = true; updateUI();
    try {
      cartState = await api.cart();
      const updates = normalizationPlan(cartState, config, active);
      if (Object.keys(updates).length) { await api.update(updates); cartState = await api.cart(); notifyCart(); }
    } catch (error) { console.error('Birthday gift cart check failed', error); }
    finally { busy = false; updateUI(); if (needsSync) { needsSync = false; sync(); } }
  };
  const checkout = async (trigger) => {
    if (busy) return;
    busy = true; updateUI();
    try {
      cartState = await api.cart();
      const updates = normalizationPlan(cartState, config, active);
      if (Object.keys(updates).length) { await api.update(updates); cartState = await api.cart(); notifyCart(); }
      if (active && qualifies(cartState, config) && !completeGift(cartState, config, active)) open(trigger);
      else window.location.assign(`${routeRoot}checkout`);
    } catch (error) { open(trigger); showError(error); }
    finally { busy = false; updateUI(); }
  };
  const choose = async (button) => {
    if (busy || button.disabled) return;
    const cta = button.querySelector?.('.brand-gwp-option__cta');
    const originalLabel = cta?.textContent;
    selectionFeedback = { button, cta, originalLabel };
    let navigating = false;
    if (cta) cta.textContent = 'Adding your gift…';
    button.dataset.wieGwpChoosing = 'true';
    busy = true; overlay.setAttribute('data-busy', ''); updateUI();
    try {
      // The chooser already has a checked cart. Validate the server cart after the add;
      // avoid redundant reads and section broadcasts before navigating to checkout.
      const selectionApi = { ...api, update: (updates) => request('cart/update.js', { updates }) };
      cartState = await selectGift(button.dataset.brandGwpVariant, config, active, selectionApi, cartLoaded ? cartState : undefined);
      // An editor change can arrive while Shopify is adding the gift.
      if (!completeGift(cartState, config, active)) {
        const updates = normalizationPlan(cartState, config, active);
        if (Object.keys(updates).length) cartState = await selectionApi.update(updates);
        throw new Error('The offer has changed. Please check your cart and try again.');
      }
      if (cta) cta.textContent = 'Opening checkout…';
      overlay.hidden = true;
      document.body.append(overlay);
      window.location.assign(`${routeRoot}checkout`);
      navigating = true;
    } catch (error) {
      overlay.hidden = false;
      showError(error);
      try { cartState = await api.cart(); notifyCart(); } catch (_) { /* Never proceed after a failed gift check. */ }
    } finally {
      if (!navigating) {
        if (cta) cta.textContent = originalLabel;
        delete button.dataset.wieGwpChoosing;
        selectionFeedback = undefined;
        busy = false; overlay.removeAttribute('data-busy'); updateUI();
        if (needsSync) { needsSync = false; sync(); }
      }
    }
  };
  document.addEventListener('shopify:section:load', (event) => {
    const nextElement = event.target?.querySelector?.('#WIEGwpConfig');
    const nextOverlay = event.target?.querySelector?.('#WIEGwpOverlay');
    if (!nextElement || !nextOverlay) return;
    let nextConfig;
    try { nextConfig = JSON.parse(nextElement.textContent); } catch (_) { return; }
    const wasOpen = !overlay.hidden;
    if (overlay !== nextOverlay) overlay.remove();
    overlay = nextOverlay;
    overlay.hidden = true;
    document.body.append(overlay);
    if (wasOpen && typeof removeTrapFocus === 'function') removeTrapFocus(returnFocus);
    config = nextConfig;
    activate();
    sync();
  });
  window.addEventListener?.('pageshow', (event) => {
    if (!event.persisted) return;
    if (selectionFeedback) {
      const { button, cta, originalLabel } = selectionFeedback;
      if (cta) cta.textContent = originalLabel;
      delete button.dataset.wieGwpChoosing;
      selectionFeedback = undefined;
    }
    busy = false; needsSync = false; overlay.removeAttribute('data-busy');
    sync().then(notifyCart);
  });
  document.addEventListener('click', (event) => {
    const closeButton = event.target.closest('[data-brand-gwp-close]');
    if (closeButton || event.target === overlay) { event.preventDefault(); close(); return; }
    const option = event.target.closest('#WIEGwpOverlay [data-brand-gwp-variant]');
    if (option && active) { event.preventDefault(); choose(option); return; }
    const trigger = event.target.closest(checkoutSelector);
    if (trigger && active) { event.preventDefault(); event.stopImmediatePropagation(); checkout(trigger); }
  }, true);
  document.addEventListener('submit', (event) => {
    if (active && event.submitter?.matches(checkoutSelector)) { event.preventDefault(); event.stopImmediatePropagation(); checkout(event.submitter); }
  }, true);
  document.addEventListener('keydown', (event) => { if (event.key === 'Escape' && !overlay.hidden) { event.stopImmediatePropagation(); close(); } }, true);

  const originalCopy = new Map();
  let originalHeroLink;
  let createdHeroText;
  const birthdayCopy = () => {
    if (!active) {
      for (const [node, text] of originalCopy) if (node.textContent !== text) node.textContent = text;
      originalCopy.clear();
      if (originalHeroLink) { originalHeroLink.node.setAttribute('href', originalHeroLink.href); originalHeroLink = undefined; }
      if (createdHeroText) { createdHeroText.remove(); createdHeroText = undefined; }
      document.querySelector('.brand-campaign-hero')?.classList.remove('wie-gwp-hero');
      return;
    }
    const setCopy = (node, text) => { if (node && typeof text === 'string') { if (!originalCopy.has(node)) originalCopy.set(node, node.textContent); if (node.textContent !== text) node.textContent = text; } };
    const hero = document.querySelector('.brand-campaign-hero');
    if (hero) {
      hero.classList.add('wie-gwp-hero');
      const heading = hero.querySelector('.brand-campaign-hero__heading');
      setCopy(heading, config.heroHeading);
      let text = hero.querySelector('.brand-campaign-hero__text');
      if (!text && heading) { text = document.createElement('div'); text.className = 'brand-campaign-hero__text rte'; heading.after(text); createdHeroText = text; }
      setCopy(text, config.heroCopy);
      const button = hero.querySelector('a.button');
      if (button) { setCopy(button, config.heroButton); originalHeroLink ||= { node: button, href: button.getAttribute('href') }; if (button.getAttribute('href') !== config.shopLink) button.setAttribute('href', config.shopLink); }
    }
    document.querySelectorAll('.brand-announcement-marquee__item, .announcement-bar__message span').forEach((node) => setCopy(node, config.announcement));
    document.querySelectorAll('.brand-collection-promo').forEach((tile) => {
      setCopy(tile.querySelector('.brand-collection-promo__eyebrow'), config.heroHeading);
      setCopy(tile.querySelector('.brand-collection-promo__heading'), config.heading);
      setCopy(tile.querySelector('.brand-collection-promo__copy'), config.offerCopy);
      setCopy(tile.querySelector('.brand-collection-promo__note'), config.terms);
    });
  };
  const boot = () => {
    birthdayCopy(); sync();
    new MutationObserver(updateUI).observe(document.body, { childList: true, subtree: true });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => { if (event.source !== 'brand-gwp-selector') sync(); });
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot, { once: true });
  else boot();
})();
